// components/common/StatusChip.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isFinite } from 'lodash';

// Color variables
const COLORS = {
  // Status colors
  success: {
    lighter: '#E9FCD4',
    light: '#AAF27F',
    main: '#54D62C',
    dark: '#229A16',
  },
  warning: {
    lighter: '#FFF7CD',
    light: '#FFE16A',
    main: '#FFC107',
    dark: '#B78103',
  },
  info: {
    lighter: '#D0F2FF',
    light: '#74CAFF',
    main: '#1890FF',
    dark: '#0C53B7',
  },
  error: {
    lighter: '#FFE7D9',
    light: '#FFA48D',
    main: '#FF4842',
    dark: '#B72136',
  },
  primary: {
    lighter: '#D1E9FC',
    light: '#76B0F1',
    main: '#2065D1',
    dark: '#103996',
  },
  grey: {
    100: '#F9FAFB',
    200: '#F4F6F8',
    300: '#DFE3E8',
    400: '#C4CDD5',
    500: '#919EAB',
    600: '#637381',
    700: '#454F5B',
    800: '#212B36',
  },
};

const StyledChip = styled(Chip)(({ variant }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 24,
  borderRadius: '6px',
  ...(variant === 'outlined' && {
    borderWidth: 1.5,
  }),
  '&.status-completed': {
    backgroundColor: COLORS.success.lighter,
    color: COLORS.success.dark,
    '&:hover': { backgroundColor: COLORS.success.lighter },
  },
  '&.status-in-progress': {
    backgroundColor: COLORS.warning.lighter,
    color: COLORS.warning.dark,
    '&:hover': { backgroundColor: COLORS.warning.lighter },
  },
  '&.status-pending': {
    backgroundColor: COLORS.info.lighter,
    color: COLORS.info.dark,
    '&:hover': { backgroundColor: COLORS.info.lighter },
  },
  '&.label-count': {
    backgroundColor: COLORS.grey[200],
    color: COLORS.grey[700],
    '&:hover': { backgroundColor: COLORS.grey[300] },
  },
  '&.progress-good': {
    backgroundColor: COLORS.success.lighter,
    color: COLORS.success.dark,
  },
  '&.progress-warning': {
    backgroundColor: COLORS.warning.lighter,
    color: COLORS.warning.dark,
  },
  '&.progress-critical': {
    backgroundColor: COLORS.error.lighter,
    color: COLORS.error.dark,
  },
  '&.type-default': {
    backgroundColor: COLORS.primary.lighter,
    color: COLORS.primary.dark,
  },
}));

export const StatusChip = ({ status, type = 'status', size = 'small', ...props }) => {
  const getStatusClassName = () => {
    if (type === 'status') {
      const statusMap = {
        Completed: 'status-completed',
        'In Progress': 'status-in-progress',
        Pending: 'status-pending',
      };
      return statusMap[status] || 'status-pending';
    }
    if (type === 'progress') {
      const value = parseFloat(status);
      if (value <= 100) return 'progress-good';
      if (value <= 120) return 'progress-warning';
      return 'progress-critical';
    }
    if (type === 'count') {
      return 'label-count';
    }
    if (type === 'type') {
      return 'type-default';
    }
    return '';
  };

  const getChipLabel = () => {
    if (type === 'progress') {
      const numericStatus = parseFloat(status);
      if (!isFinite(numericStatus)) {
        return '0%';
      }
      return `${Math.round(numericStatus)}%`;
    }
    if (type === 'count') {
      const count = parseInt(status, 10);
      const suffix = count === 1 ? '' : 's';
      return `${count} task${suffix}`;
    }
    return status;
  };

  return <StyledChip label={getChipLabel()} className={getStatusClassName()} size={size} {...props} />;
};

StatusChip.propTypes = {
  status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  type: PropTypes.oneOf(['status', 'progress', 'count', 'type']),
  size: PropTypes.oneOf(['small', 'medium']),
};
